import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { policiesService } from "../../services/policies";

const initialState = {
  items: [],
  loading: false,
  error: null,
  duplicateWarning: {
    show: false,
    message: "",
  },
};

export const fetchAddresses = createAsyncThunk(
  "addresses/fetchAddresses",
  async (policyId, { rejectWithValue }) => {
    try {
      const response = await policiesService.fetchAddresses(policyId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch addresses");
    }
  }
);

export const createAddress = createAsyncThunk(
  "addresses/createAddress",
  async ({ policyId, data }, { rejectWithValue }) => {
    try {
      const response = await policiesService.createAddress(policyId, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to create address");
    }
  }
);

export const updateAddress = createAsyncThunk(
  "addresses/updateAddress",
  async ({ policyId, addressId, data }, { rejectWithValue }) => {
    try {
      const response = await policiesService.updateAddress(
        policyId,
        addressId,
        data
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to update address");
    }
  }
);

export const deleteAddress = createAsyncThunk(
  "addresses/deleteAddress",
  async ({ policyId, addressId }, { rejectWithValue }) => {
    try {
      const response = await policiesService.deleteAddress(
        policyId,
        addressId
      );
      return { addressId, ...response };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to delete address");
    }
  }
);

export const checkDuplicate = createAsyncThunk(
  "addresses/checkDuplicate",
  async ({ policyId, data }, { rejectWithValue }) => {
    try {
      const response = await policiesService.checkDuplicate(policyId, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Failed to check duplicate");
    }
  }
);

const addressSlice = createSlice({
  name: "addresses",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearDuplicateWarning: (state) => {
      state.duplicateWarning = {
        show: false,
        message: "",
      };
    },
    setDuplicateWarning: (state, action) => {
      state.duplicateWarning = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAddresses.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both wrapped and unwrapped responses
        const payload = action.payload;
        if (payload.data && Array.isArray(payload.data)) {
          state.items = payload.data;
        } else if (Array.isArray(payload)) {
          state.items = payload;
        } else {
          state.items = [];
        }
      })
      .addCase(fetchAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(createAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAddress.fulfilled, (state, action) => {
        state.loading = false;
        const newAddress = action.payload.data || action.payload;
        state.items.push(newAddress);
      })
      .addCase(createAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(updateAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAddress.fulfilled, (state, action) => {
        state.loading = false;
        const updatedAddress = action.payload.data || action.payload;
        const index = state.items.findIndex((a) => a.id === updatedAddress.id);
        if (index !== -1) {
          state.items[index] = updatedAddress;
        }
      })
      .addCase(updateAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(deleteAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((a) => a.id !== action.payload.addressId);
      })
      .addCase(deleteAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(checkDuplicate.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkDuplicate.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.isDuplicate) {
          state.duplicateWarning = {
            show: true,
            message: action.payload.message || "This address already exists",
          };
        }
      })
      .addCase(checkDuplicate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearDuplicateWarning, setDuplicateWarning } = addressSlice.actions;

export const selectAddresses = (state) => state.addresses.items;
export const selectAddressesLoading = (state) => state.addresses.loading;
export const selectAddressesError = (state) => state.addresses.error;
export const selectDuplicateWarning = (state) => state.addresses.duplicateWarning;

export default addressSlice.reducer;
